import { useQuery } from '@tanstack/react-query';

import React, { useState } from 'react';

import { Alert, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, MenuItem, Stack, TextField } from '@mui/material';

import { useApiClient } from '@@common';

import { CreateEmbedDefinitionRequest, EmbedDefinitionClient, EmbedDefinitionModel, UserInterfaceClient } from '../../../api/api';
import ChangeSetSelectionDialog from './ChangeSetSelectionDialog';
import { getEmbedViewName, useEmbedApp } from './useEmbedApp';

export default function EmbedDefinitionDialog({
	definition,
	changeSetId,
	onClose,
	onSaved,
}: {
	definition: EmbedDefinitionModel | null;
	changeSetId?: string;
	onClose: () => void;
	onSaved: (definition: EmbedDefinitionModel) => void;
}) {
	const client = useApiClient(EmbedDefinitionClient);
	const apps = useApiClient(UserInterfaceClient);
	const [request, setRequest] = useState<CreateEmbedDefinitionRequest>(() =>
		definition
			? { ...definition }
			: {
					name: '',
					code: '',
					description: '',
					appDefinitionId: '',
					allowedViewIds: [],
					defaultViewId: '',
				},
	);
	const [source, setSource] = useState<{ id: string; name: string }>();
	const [selectSource, setSelectSource] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState('');
	const appList = useQuery({
		queryKey: ['embedApps', source?.id],
		enabled: !!source,
		queryFn: async () => {
			const result = [];
			for (let page = 0; ; page++) {
				const batch = await apps.search(source!.id, undefined, undefined, page, 100);
				result.push(...batch.items);
				if (!batch.hasNextPage) {
					break;
				}
			}
			return result;
		},
	});
	const app = useEmbedApp(request.appDefinitionId, source?.id ?? changeSetId);
	const views = { ...app, data: source ? app.data?.views : undefined };
	const save = async () => {
		setBusy(true);
		setError('');
		try {
			const saved = definition ? await client.update(definition.id, request) : await client.create(request);
			onSaved(saved);
		} catch (failure) {
			setError(failure instanceof Error ? failure.message : 'App Embed could not be saved.');
		} finally {
			setBusy(false);
		}
	};
	return (
		<>
			<Dialog open onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
				<DialogTitle>{definition ? 'Edit App Embed' : 'Create App Embed'}</DialogTitle>
				<DialogContent>
					<Stack spacing={2} sx={{ pt: 1 }}>
						<TextField label="Name" value={request.name} onChange={(e) => setRequest({ ...request, name: e.target.value })} />
						<TextField
							label="Code"
							value={request.code}
							disabled={!!definition}
							helperText="Letters, digits and hyphens. The code stays the same after creation."
							onChange={(e) => setRequest({ ...request, code: e.target.value })}
						/>
						<TextField label="Description" value={request.description ?? ''} onChange={(e) => setRequest({ ...request, description: e.target.value })} />
						<Button onClick={() => setSelectSource(true)}>{source ? 'Source ChangeSet: ' + source.name : 'Select source ChangeSet'}</Button>
						<Alert severity="info">Use a source ChangeSet to select views. Deployment validates these views against each target environment.</Alert>
						<TextField
							select
							label="App"
							value={request.appDefinitionId}
							disabled={!source}
							onChange={(e) => setRequest({ ...request, appDefinitionId: e.target.value, allowedViewIds: [], defaultViewId: '' })}
						>
							{request.appDefinitionId && !appList.data?.some((app) => app.definitionId === request.appDefinitionId) && (
								<MenuItem value={request.appDefinitionId}>{app.data?.name || request.appDefinitionId}</MenuItem>
							)}
							{appList.data
								?.filter((app) => app.definitionId)
								.map((app) => (
									<MenuItem key={app.definitionId} value={app.definitionId}>
										{app.name}
									</MenuItem>
								))}
						</TextField>
						{appList.isError && (
							<Alert severity="error">
								Apps could not be loaded. <Button onClick={() => void appList.refetch()}>Retry</Button>
							</Alert>
						)}
						{views.isError && <Alert severity="error">{String(views.error)}</Alert>}
						{views.data?.some((view) => view.RequiresAuthentication !== false) && (
							<Alert severity="warning">
								This App contains views requiring sign-in. You can select any initial view, but selected views must have authentication disabled before
								deployment. Shared resources and React bundles may contain code used by other views.
							</Alert>
						)}
						{views.data?.some((view) => request.allowedViewIds.includes(view.Id) && view.RequiresAuthentication !== false) && (
							<Alert severity="warning">A selected view requires sign-in. Disable Requires Authentication on that view before deploying this App Embed.</Alert>
						)}
						{views.data?.map((view) => (
							<FormControlLabel
								key={view.Id}
								label={getEmbedViewName(view, view.Id) + (view.RequiresAuthentication !== false ? ' (requires sign-in)' : '')}
								control={
									<Checkbox
										checked={request.allowedViewIds.includes(view.Id)}
										onChange={(_, checked) => {
											const ids = checked ? [...request.allowedViewIds, view.Id] : request.allowedViewIds.filter((id) => id !== view.Id);
											setRequest({
												...request,
												allowedViewIds: ids,
												defaultViewId: ids.includes(request.defaultViewId) ? request.defaultViewId : (ids[0] ?? ''),
											});
										}}
									/>
								}
							/>
						))}
						<TextField
							select
							label="Initial view"
							value={request.defaultViewId}
							onChange={(e) =>
								setRequest({
									...request,
									defaultViewId: e.target.value,
									allowedViewIds: request.allowedViewIds.includes(e.target.value) ? request.allowedViewIds : [...request.allowedViewIds, e.target.value],
								})
							}
						>
							{request.defaultViewId && !views.data?.some((view) => view.Id === request.defaultViewId) && (
								<MenuItem value={request.defaultViewId}>
									{getEmbedViewName(
										app.data?.views.find((view) => view.Id === request.defaultViewId),
										request.defaultViewId,
									)}
								</MenuItem>
							)}
							{views.data?.map((view) => (
								<MenuItem key={view.Id} value={view.Id}>
									{getEmbedViewName(view, view.Id) + (view.RequiresAuthentication !== false ? ' (requires sign-in)' : '')}
								</MenuItem>
							))}
						</TextField>
						{error && <Alert severity="error">{error}</Alert>}
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button disabled={busy} onClick={onClose}>
						Cancel
					</Button>
					<Button
						disabled={
							busy ||
							!request.name.trim() ||
							!request.code ||
							!request.defaultViewId ||
							(!!source && (views.isError || !views.data?.length)) ||
							(!!source && (!views.data || !request.allowedViewIds.every((id) => views.data?.some((view) => view.Id === id)))) ||
							(!definition && !views.data)
						}
						onClick={() => void save()}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>
			<ChangeSetSelectionDialog
				open={selectSource}
				onClose={() => setSelectSource(false)}
				onSelect={(value) => {
					setSource(value);
					setSelectSource(false);
				}}
			/>
		</>
	);
}
